import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { useWorkspaces } from '@/hooks/useWorkspaces';

/**
 * For the currently-visible contacts, fetch each one's OLDEST outstanding activity
 * (scheduled AND NOT completed) start_date — past-overdue wins, else the soonest upcoming.
 *
 * Powers the contact-list "Event" line. Kept out of `filter_contacts` (which returns from
 * a view with no activity join) so the hot-path RPC is untouched: this is a small,
 * pagination-scoped read of only the visible ids. Activities RLS (`activities_access`)
 * scopes reads to the caller's active-membership workspaces, so the direct select is safe.
 *
 * Returns a map: contact_id -> oldest outstanding activity start_date.
 */
export function useVisibleContactActivities(contactIds: string[]) {
  const { currentWorkspace } = useWorkspaces();
  const workspaceId = currentWorkspace?.id;

  // Sorted so the query key is order-independent (a re-sort of the same page doesn't refetch).
  const ids = useMemo(() => [...contactIds].sort(), [contactIds]);

  return useQuery({
    queryKey: queryKeys.activities.oldestOutstandingBatch(workspaceId, ids),
    enabled: !!workspaceId && ids.length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<Record<string, string>> => {
      if (!workspaceId || ids.length === 0) return {};
      const { data, error } = await supabase
        .from('activities')
        .select('contact_id, start_date')
        .eq('workspace_id', workspaceId)
        .in('contact_id', ids)
        .eq('scheduled', true)
        .eq('completed', false)
        .order('start_date', { ascending: true });
      if (error) throw error;

      // Rows are ordered oldest-first, so the FIRST row seen per contact is its oldest.
      const map: Record<string, string> = {};
      for (const row of data ?? []) {
        if (row.contact_id && !map[row.contact_id]) {
          map[row.contact_id] = row.start_date;
        }
      }
      return map;
    },
  });
}
